<template>
  <li class="menu-item-wrapper">
    <div
      class="sidebar-item"
      @click="handleItemClick"
      :class="{ 'has-children': hasChildren, 'expanded': isExpanded, 'active': isActive }"
    >
      <!-- Icon -->
      <i v-if="item.menu_icon" class="sidebar-icon" :class="[item.menu_icon]"></i>
      <i v-else class="sidebar-icon fas fa-circle"></i>

      <!-- Menu Name -->
      <span class="menu-name">{{ item.menu_name }}</span>

      <!-- Expand/Collapse Arrow for items with children -->
      <i
        v-if="hasChildren"
        class="expand-icon fas"
        :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
      ></i>
    </div>

    <!-- Children Items (Nested) -->
    <ul v-if="hasChildren && isExpanded" class="submenu">
      <SidebarMenuItem
        v-for="child in children"
        :key="child.auto_id"
        :item="child"
        :allMenuData="allMenuData"
        @navigate="handleChildNavigation"
      />
    </ul>
  </li>
</template>

<script>
export default {
  name: 'SidebarMenuItem',
  props: {
    item: {
      type: Object,
      required: true,
    },
    allMenuData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    children() {
      // Get all items where parent_id matches this item's menuid, sorted by order_id
      if (!this.allMenuData || !this.item) return [];
      
      return this.allMenuData
        .filter(menuItem => menuItem.parent_id === this.item.menuid)
        .sort((a, b) => a.order_id - b.order_id);
    },
    hasChildren() {
      return this.children && this.children.length > 0;
    },
    isActive() {
      // Check if this menu item matches the current route
      const routeName = this.$route.name;
      const menuRouteName = this.item.menu_action
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^\w]+/g, '');
      
      return routeName === menuRouteName;
    },
  },
  watch: {
    '$route.name'() {
      // Auto-expand parent if any child is active
      if (!this.isActive && this.hasChildren) {
        this.isExpanded = false;
      } else if (this.isActive && this.hasChildren) {
        this.isExpanded = true;
      }
    },
  },
  methods: {
    handleItemClick() {
      if (this.hasChildren) {
        // Toggle expand/collapse if has children
        this.isExpanded = !this.isExpanded;
        
        // Emit expand event to parent for accordion behavior
        if (this.isExpanded) {
          this.$emit('item-expanded', this.item);
        }
      } else {
        // Navigate if no children
        this.navigateToItem();
      }
    },
    navigateToItem() {
      this.$emit('navigate', this.item);
    },
    handleChildNavigation(item) {
      // Pass navigation event to parent
      this.$emit('navigate', item);
    },
    collapseMenu() {
      this.isExpanded = false;
    },
  },
};
</script>

<style scoped>
.menu-item-wrapper {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  border-radius: 6px;
  margin: 4px 0;
  user-select: none;
  gap: 8px;
}

.sidebar-item:hover {
  background-color: rgba(97, 218, 251, 0.1);
  border-left-color: #61dafb;
}

.sidebar-item.active {
  background-color: rgba(97, 218, 251, 0.2);
  border-left-color: #61dafb;
  font-weight: 600;
}

.sidebar-item.has-children {
  justify-content: space-between;
}

.sidebar-icon {
  min-width: 20px;
  flex-shrink: 0;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-name {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.expand-icon {
  font-size: 12px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 10px;
  border-left: 2px solid rgba(97, 218, 251, 0.3);
  margin-left: 8px;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.submenu .sidebar-item {
  font-size: 13px;
  margin: 2px 0;
}

/* Nested submenu styling */
.submenu .submenu {
  padding-left: 10px;
  margin-left: 8px;
  border-left: 2px solid rgba(97, 218, 251, 0.2);
}

/* Deep nesting support */
.submenu .submenu .sidebar-item {
  padding-left: 44px;
  font-size: 12px;
}

.submenu .submenu .submenu .sidebar-item {
  padding-left: 60px;
  font-size: 11px;
}
</style>
